import { useId, useState, type RefObject } from "react";
import { styled } from "@pigment-css/react";
import { ChevronRightThin } from "@acto/web-common/components";
import { useTranslations } from "@acto/web-common/locales";
import { Collapse } from "@acto/taskpane/field-sales-2/components/primitives/Collapse";
import { RowList } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { PinnedCaption } from "@acto/taskpane/field-sales-2/components/primitives/PinnedCaption";
import { HiddenProductRow } from "./HiddenProductRow";

export interface HiddenProductView {
  id: string;
  name: string;
  metaLabel: string;
}

const Trigger = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[4],
  padding: 0,
  border: "none",
  background: "transparent",
  appearance: "none",
  WebkitAppearance: "none",
  cursor: "pointer",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["168"],
  color: theme.fieldSalesTwo.colors.neutral[800],
  "&:focus-visible": {
    outline: `${theme.strokeWidth.regular} solid ${theme.fieldSalesTwo.colors.neutral[700]}`,
    outlineOffset: theme.fieldSalesTwo.spacing[2],
    borderRadius: theme.fieldSalesTwo.radius["16"],
  },
}));

const CaptionContainingBlock = styled("div")({ position: "relative" });

const PinnedTrigger = styled(PinnedCaption)(({ theme }) => ({
  paddingTop: theme.fieldSalesTwo.spacing[8],
  paddingBottom: theme.fieldSalesTwo.spacing[12],
  background: theme.fieldSalesTwo.colors.surface.glassFlat,
}));

// Padding, not margin: Collapse measures its content box, and a child's margin escapes it.
const DividerPad = styled("div")(({ theme }) => ({
  paddingTop: theme.fieldSalesTwo.spacing[16],
  paddingBottom: theme.fieldSalesTwo.spacing[12],
}));

const Caret = styled("span")<{ expanded: boolean }>(({ theme }) => ({
  display: "flex",
  transition: `transform ${theme.fieldSalesTwo.motion.duration.fast} ${theme.fieldSalesTwo.motion.easing.standard}`,
  transform: "rotate(90deg)",
  "& svg": { width: "14px", height: "auto" },
  variants: [{ props: { expanded: true }, style: { transform: "rotate(-90deg)" } }],
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));

// Collapse clips to its box. The negative inline margin, offset by the list's matching inline
// padding, widens that box so row content drawn past the rows' inline edges is not cut off.
const BleedingRegion = styled("div")(({ theme }) => ({
  marginInline: `-${theme.fieldSalesTwo.spacing[8]}`,
}));

const List = styled(RowList)(({ theme }) => ({
  gap: 0,
  paddingTop: theme.fieldSalesTwo.spacing[8],
  paddingInline: theme.fieldSalesTwo.spacing[8],
  '&[data-pinnable="true"]': {
    paddingTop: 0,
    paddingBottom: `calc(${theme.fieldSalesTwo.spacing[16]} + ${theme.fieldSalesTwo.spacing[12]})`,
  },
}));

export interface HiddenProductsDisclosureProps {
  products: HiddenProductView[];
  onRestore: (id: string) => void;
  restoringIds?: ReadonlySet<string>;
  defaultExpanded?: boolean;
  /** The scrollport the count pins under while its rows scroll past. Without it the count scrolls away. */
  triggerScrollRef?: RefObject<HTMLDivElement | null>;
  /** Draws a divider above the count while the list is open; it scrolls away under a pinned count. */
  leadingDivider?: boolean;
}

export function HiddenProductsDisclosure({
  products,
  onRestore,
  restoringIds,
  defaultExpanded = false,
  triggerScrollRef,
  leadingDivider = false,
}: HiddenProductsDisclosureProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.productHide");
  const regionId = useId();
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (products.length === 0) return null;

  const trigger = (
    <Trigger
      type="button"
      onClick={() => setExpanded((current) => !current)}
      aria-expanded={expanded}
      aria-controls={regionId}
    >
      {t("hiddenCount", { count: products.length })}
      <Caret expanded={expanded} aria-hidden="true">
        <ChevronRightThin strokeWidth={1.5} />
      </Caret>
    </Trigger>
  );

  return (
    <CaptionContainingBlock>
      {leadingDivider && (
        <Collapse open={expanded}>
          <DividerPad>
            <Divider />
          </DividerPad>
        </Collapse>
      )}
      {triggerScrollRef ? <PinnedTrigger scrollRef={triggerScrollRef}>{trigger}</PinnedTrigger> : trigger}
      <BleedingRegion>
        <Collapse open={expanded} id={regionId}>
          <List role="list" data-pinnable={!!triggerScrollRef}>
            {products.map((product) => (
              <HiddenProductRow
                key={product.id}
                name={product.name}
                metaLabel={product.metaLabel}
                onRestore={() => onRestore(product.id)}
                isRestoring={restoringIds?.has(product.id)}
              />
            ))}
          </List>
        </Collapse>
      </BleedingRegion>
    </CaptionContainingBlock>
  );
}
